import React, { useState } from 'react';
import {
  User,
  Mail,
  Phone,
  Store,
  CheckCircle,
  AlertCircle,
  Loader,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';

const VendorApplicationForm = ({ logoUrl, onClose }) => {
  const { currentUser } = useAuth();

  const [formData, setFormData] = useState({
    applicantName: currentUser ? (currentUser.displayName || currentUser.email || '') : '',
    applicantEmail: currentUser ? currentUser.email || '' : '',
    applicantPhone: currentUser ? currentUser.phone || '' : '',
    businessName: '',
    businessType: '',
    website: '',
    socialLinks: '',
    productsDescription: '',
    notes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    if (!formData.applicantName.trim()) return 'Please enter your name';
    if (!formData.applicantEmail.trim()) return 'Please enter your email';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.applicantEmail)) return 'Please enter a valid email address';
    if (!formData.businessName.trim()) return 'Please enter your business name';
    if (!formData.productsDescription.trim()) return 'Please describe what you plan to sell';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const vErr = validate();
    if (vErr) {
      setError(vErr);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const res = await api.post('/vendors/applications', formData);
      if (res.ok) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit application');
      }
    } catch (err) {
      console.error('Vendor application error:', err);
      setError(err.message || 'Failed to submit vendor application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-3 sm:p-4 animate-fadeIn" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="bg-white rounded-2xl sm:rounded-3xl max-w-sm sm:max-w-md w-full p-5 sm:p-6 text-center shadow-2xl border border-gray-100 my-auto animate-scaleUp">
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg overflow-hidden">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="BOW Logo"
                className="w-full h-full object-contain"
              />
            ) : (
              <CheckCircle className="w-8 h-8 text-white" />
            )}
          </div>
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1.5">Application Submitted!</h3>
          <p className="text-xs sm:text-sm text-gray-600 mb-4 px-2 leading-relaxed">
            Thanks for applying as a vendor. Our team will review your application and reach out soon.
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 sm:py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-xs sm:text-sm shadow-md transition-all duration-200 hover:scale-105 w-full"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2.5 sm:p-4 md:p-6 bg-black/60 backdrop-blur-sm overflow-y-auto animate-fadeIn"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-lg sm:max-w-2xl max-h-[85vh] sm:max-h-[90vh] flex flex-col bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 overflow-hidden my-auto animate-scaleUp transform transition-all" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="p-3.5 sm:p-6 border-b border-gray-100 flex items-center justify-between shrink-0 bg-white">
          <div className="flex items-center flex-1 min-w-0 pr-3">
            {logoUrl && (
              <img
                src={logoUrl}
                alt="BOW Logo"
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full mr-2.5 sm:mr-3 object-contain shrink-0"
              />
            )}
            <div className="min-w-0">
              <h3 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 tracking-tight truncate">Vendor Application</h3>
              <p className="text-[11px] sm:text-sm text-gray-500 mt-0.5 truncate">
                Apply to participate as a vendor at BOW events
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-1.5 sm:p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
            aria-label="Close modal"
            type="button"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-3.5 sm:p-6 space-y-2.5 sm:space-y-4 flex-1 overflow-y-auto min-h-0 overscroll-y-contain">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 sm:p-3 flex items-start">
              <AlertCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <h4 className="text-red-800 font-semibold text-xs sm:text-sm">Error</h4>
                <p className="text-red-700 text-[11px] sm:text-xs mt-0.5 break-words">{error}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 min-w-0">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-primary-600" />
                Name *
              </label>
              <input
                type="text"
                name="applicantName"
                value={formData.applicantName}
                onChange={handleChange}
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-primary-600" />
                Email *
              </label>
              <input
                type="email"
                name="applicantEmail"
                value={formData.applicantEmail}
                onChange={handleChange}
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 min-w-0">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-primary-600" />
                Phone
              </label>
              <input
                type="tel"
                name="applicantPhone"
                value={formData.applicantPhone}
                onChange={handleChange}
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                <Store className="w-3.5 h-3.5 text-primary-600" />
                Business Name *
              </label>
              <input
                type="text"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 min-w-0">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Business Type</label>
              <input
                type="text"
                name="businessType"
                value={formData.businessType}
                onChange={handleChange}
                placeholder="Food / Craft / Services / etc."
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://..."
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Social Links</label>
            <input
              type="text"
              name="socialLinks"
              value={formData.socialLinks}
              onChange={handleChange}
              placeholder="Instagram / Facebook / YouTube links"
              className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              What will you sell? *
            </label>
            <textarea
              name="productsDescription"
              value={formData.productsDescription}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm resize-none"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm resize-none"
            />
          </div>

          {/* Action buttons */}
          <div className="pt-2.5 sm:pt-3 flex items-center justify-end gap-2.5 border-t border-gray-100 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2 sm:py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-xs sm:text-sm shadow-md transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                'Submit Vendor Application'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorApplicationForm;
